import React, { useEffect, useRef, useState } from "react";
import styles from './Students.module.scss';
import LoadingOverlay from "../../../components/loadingOverlay/LoadingOverlay";
import PageTitle from "../../../components/pageTitle/PageTitle";
import DropDownButton from "../../../components/dropDownButton/DropDownButton";
import Search from "../../../components/search/Search";
import LoadMore from "../../../components/loadMore/loadMore";
import StudentAllocationCard from "../../../components/studentAllocationCard/StudentAllocationCard";
import { useToasts } from "../../../contexts/ToastContext";
import { useAuth } from "../../../contexts/AuthContext";
import Modal from "../../../components/modal/Modal";
import UploadForm from "../../../components/uploadForm/UploadForm";
import axiosClient from '../../../api/axiosClient';
import { format } from "date-fns";
import { useNavigate } from "react-router-dom";

const Students = () => {
  const limit = 20;
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadMoreLoading, setIsLoadMoreLoading] = useState(false);
  const [students, setStudents] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(100);
  const [isUploadLoading, setIsUploadLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [refetchTrigger, setRefetchTrigger] = useState(false);
  const { showToast } = useToasts();
  const { role } = useAuth();
  const progressRef = useRef(0);
  const navigate = useNavigate();
  const studentTemplateUrl = "https://maestrostoragedev.blob.core.windows.net/maestro/files/students_template.xlsx"

  useEffect(() => {
    const fetchData = async () => {
      if(page === 1) {
        setIsLoading(true);
      } else {
        setIsLoadMoreLoading(true);
      }
      await axiosClient.get(`users/student-management/students`, {
        params: {
          limit: limit,
          page: page,
          search: search || undefined,
        }
      })
      .then((response) => {
        if(response.data?.students) {
          if(page === 1) {
            setStudents([]);
          }
          setStudents((prevData) => {
            return [...prevData, ...response.data.students.map((student) => ({
              id: student.id,
              profilePicture: student?.profilePicture ?? null,
              firstName: student?.firstName ?? '',
              lastName: student?.lastName ?? '',
              data: [
                {
                  label: 'Instructor',
                  value: `${student?.allocation?.instructor?.firstName ?? ''} ${student?.allocation?.instructor?.lastName ?? ''}`
                },
                {
                  label: 'Speciality',
                  value: student?.specialities.length > 0 ?
                    `${student?.specialities[0].name} ${student?.specialities.length > 1
                      ? `+${student?.specialities.length - 1}` : ''}` :
                    '',
                  tooltip: student?.specialities.length > 1 ?
                    student?.specialities.map(item => item.name).join(', ') :
                    ''
                },
                {
                  label: 'Semester',
                  value: student?.semester?.semester ?? ''
                },
                {
                  label: 'Year',
                  value: student?.year?.year ?? ''
                },
                {
                  label: 'Start Date',
                  value: student?.allocation?.startDate ? format(student?.allocation?.startDate, "MM/dd/yyyy") : ''
                },
                {
                  label: 'End Date',
                  value: student?.allocation?.endDate ? format(student?.allocation?.endDate, "MM/dd/yyyy") : ''
                },
                {
                  label: 'Graduation Date',
                  value: student?.rotationEndDate ? format(student?.rotationEndDate, "MM/dd/yyyy") : ''
                },
              ],
            }))];
          });
          setTotalPages(response.data.totalPages);
        }
      })
      .catch((error) => {
        showToast(error.message, 'error');
      })
      .finally(() => {
        setIsLoading(false);
        setIsLoadMoreLoading(false);
      });
    };

    fetchData();
  }, [page, search, showToast, refetchTrigger]);

  const loadMore = () => {
    setPage(page + 1);
  };

  const handleSearch = (value) => {
    setSearch(value);
    setPage(1);
    setStudents([]);
  };

  const showProgress = () => {
    setUploadProgress(0);
    progressRef.current = 0;

    const interval = setInterval(() => {
      progressRef.current = progressRef.current + 1;
      setUploadProgress(progressRef.current);
      if (progressRef.current >= 100) {
        clearInterval(interval);
      }
    }, 10);

    return () => clearInterval(interval);
  };

  const handleUpload = async (file) => {
    const formData = new FormData();
    if(file) {
      formData.append('studentInfoFile', file);
    }
    setIsUploadLoading(true);
    showProgress();
    await axiosClient.post(`students/upload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      }
    })
    .then((response) => {
      showToast(response.message);
      setShowUploadModal(false);
      setRefetchTrigger(!refetchTrigger);
    })
    .catch((error) => {
      showToast(error.message, 'error');
    })
    .finally(() => {
      setIsUploadLoading(false);
    });
  };

  return (
    <>
      {isLoading && <LoadingOverlay />}
      <div className={styles.header}>
        <PageTitle label="Student Management" />
        <div className={styles.headerActions}>
          <Search onSubmit={handleSearch} />
        </div>
      </div>
      <div className={styles.container}>
        {role === 'Program Director' && (
          <div className={styles.topBar}>
            <DropDownButton
              label="Manage Students"
              disabled={isLoading}
              items={[
                {
                  label: 'Add New Student',
                  onClick: () => navigate('/students/create')
                },
                {
                  label: 'Bulk Upload Students',
                  onClick: () => setShowUploadModal(true)
                }
              ]}
            />
          </div>
        )}
        {students.length > 0 ? (
          <div className={styles.cardList}>
            {students.map((student, index) => (
              <div className={styles.cardItem} key={`student-${index}`}>
                <StudentAllocationCard
                  {...student}
                  onClick={() => navigate(`/students/summary/${student.id}`)}
                />
              </div>
            ))}
            {(isLoadMoreLoading || page < totalPages)
              && <LoadMore isLoading={isLoadMoreLoading} onClick={loadMore} />}
          </div>
        ) : !isLoading && (
          <div className={styles.emptyStudents}>
            <p>You have no students at this time.</p>
          </div>
        )}
      </div>
      {showUploadModal && (
        <Modal>
          <UploadForm
            isUploading={isUploadLoading}
            uploadProgress={uploadProgress}
            onClose={() => setShowUploadModal(false)}
            onSubmit={handleUpload}
            buttonLabel="Import Students"
            templateFileUrl={studentTemplateUrl}
          />
        </Modal>
      )}
    </>
  );
};

export default Students;
